import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Settings, PlusCircle, List, RefreshCw, LogOut, User, MapPin, ChevronDown, Users, BarChart3, Receipt, AlertTriangle, Package, ClipboardCheck, Menu, X, MessageSquare, CalendarDays } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import axios from 'axios';
import ClockInPrompt from './ClockInPrompt';
import ChatRoom from './ChatRoom';
import useNoIndex from '../hooks/useNoIndex';
import { posUrl } from '../pages/storefront/storeBase';

const API = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  useNoIndex();
  const { hasUnsavedChanges, unsavedMessage, clearUnsaved } = useUnsavedChanges();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [pendingLabelCount, setPendingLabelCount] = useState(0);
  const [pendingCheckCount, setPendingCheckCount] = useState(0);
  const [pendingPhotoCount, setPendingPhotoCount] = useState(0);
  // Mobile slide-out nav drawer state — only ever opened on `< md`
  // viewports via the hamburger button in the top bar. Closes on route
  // change, body-click outside the drawer, and Escape.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { 
    user, 
    token,
    logout, 
    hasPermission,
    locations,
    selectedLocationId,
    selectLocation,
    getAccessibleLocations,
    getSelectedLocation,
    locationsLoading,
    shopName,
    isImpersonating,
    impersonatorName,
    stopImpersonating,
  } = useAuth();
  const { openChat, unread: chatUnread, flash: chatFlash } = useChat();
  const chatUnreadTotal = chatUnread?.total || 0;
  
  // Handle navigation with unsaved changes check
  const handleNavigation = (path, e) => {
    const full = posUrl(path);
    if (hasUnsavedChanges && location.pathname !== full) {
      e.preventDefault();
      setPendingNavigation(full);
      setShowUnsavedDialog(true);
    }
  };

  const confirmNavigation = () => {
    clearUnsaved();
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const cancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };
  
  // Unread articles count for Employee Zone badge
  const [unreadCount, setUnreadCount] = useState(0);
  
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/employee-zone/articles/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  }, [token]);
  
  useEffect(() => {
    fetchUnreadCount();
    // Refresh every 60 seconds
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Auto-close the mobile nav drawer when the route changes and on Escape.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  // Fetch pending labels count + inventory checks count
  useEffect(() => {
    const fetchLabelCount = async () => {
      try {
        const res = await axios.get(`${API}/api/pending-labels/count`);
        setPendingLabelCount(res.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    const fetchCheckCount = async () => {
      if (!selectedLocationId) return;
      try {
        const res = await axios.get(`${API}/api/inventory-checks/pending-count`, { params: { location_id: selectedLocationId } });
        setPendingCheckCount(res.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    const fetchPhotoCount = async () => {
      try {
        const res = await axios.get(`${API}/api/daily-tasks/missing-photos/count`);
        setPendingPhotoCount(res.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchLabelCount();
    fetchCheckCount();
    fetchPhotoCount();
    const interval = setInterval(() => { fetchLabelCount(); fetchCheckCount(); fetchPhotoCount(); }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId]);

  
  const isActive = (path) => location.pathname === posUrl(path);

  const handleLogout = async () => {
    await logout();
    navigate(posUrl('/login'));
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin': return 'bg-purple-600 text-purple-100';
      case 'Manager': return 'bg-blue-600 text-blue-100';
      case 'Cashier': return 'bg-gray-600 text-gray-100';
      default: return 'bg-gray-600 text-gray-100';
    }
  };

  const accessibleLocations = getAccessibleLocations();
  const selectedLocation = getSelectedLocation();
  
  // Display name: use shop name if available, fallback to "ShopSoft"
  const displayName = shopName || 'ShopSoft';
  const displayInitial = displayName.charAt(0).toUpperCase();
  
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
      {/* Impersonation banner — visible across every page while a
          manager is logged in as another staff member. Click "Switch
          back" to restore the original (admin) session. */}
      {isImpersonating && (
        <div
          className="bg-amber-500 text-slate-900 px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium"
          data-testid="impersonation-banner"
        >
          <span role="img" aria-label="warning">⚠️</span>
          <span>
            Viewing as <strong>{user?.full_name || user?.username}</strong>
            {impersonatorName && <> · signed in by <strong>{impersonatorName}</strong></>}
          </span>
          <Button
            size="sm"
            onClick={stopImpersonating}
            className="bg-slate-900 hover:bg-slate-800 text-white h-7 px-3 text-xs"
            data-testid="impersonation-switch-back"
          >
            Switch back
          </Button>
        </div>
      )}
      <nav className="border-b h-16" style={{ 
        background: 'rgba(255, 255, 255, 0.05)', 
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div className="h-full px-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 md:space-x-6">
            {/* Hamburger — visible only on phones (<md). The full inline
                nav further right is hidden on phones via `hidden md:flex`. */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden text-gray-300 hover:text-white p-2 -ml-2 rounded-lg hover:bg-white/5 transition-colors"
              aria-label="Open navigation"
              data-testid="mobile-nav-open"
            >
              <Menu size={22} />
            </button>

            <Link to={posUrl('/')} className="flex items-center space-x-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)'
              }}>
                <span className="text-xl font-bold text-white">{displayInitial}</span>
              </div>
              <span className="text-lg font-bold" style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>{displayName}</span>
            </Link>
            
            <div className="hidden md:flex space-x-1">
              {/* 1. POS - Default Landing */}
              {hasPermission('use_pos') && (
                <Link 
                  to={posUrl('/')} 
                  data-testid="nav-pos"
                  onClick={(e) => handleNavigation('/', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/') 
                      ? 'bg-emerald-500 bg-opacity-20 text-emerald-400 border border-emerald-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <ShoppingCart size={16} />
                  <span className="font-medium">POS</span>
                </Link>
              )}
              
              {/* 2. Orders (POS Order History) */}
              {hasPermission('use_pos') && (
                <Link 
                  to={posUrl('/orders')} 
                  data-testid="nav-orders"
                  onClick={(e) => handleNavigation('/orders', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/orders') 
                      ? 'bg-emerald-500 bg-opacity-20 text-emerald-400 border border-emerald-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <Receipt size={16} />
                  <span className="font-medium">Orders</span>
                </Link>
              )}
              
              {/* 2.5 Customers */}
              {(hasPermission('view_customers') || hasPermission('manage_customers')) && (
                <Link 
                  to={posUrl('/customers')} 
                  data-testid="nav-customers"
                  onClick={(e) => handleNavigation('/customers', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/customers') 
                      ? 'bg-violet-500 bg-opacity-20 text-violet-400 border border-violet-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <Users size={16} />
                  <span className="font-medium">Customers</span>
                </Link>
              )}
              
              {/* 3. Trade In (previously New Batch) */}
              {hasPermission('create_batches') && (
                <Link 
                  to={posUrl('/new-batch')} 
                  data-testid="nav-trade-in"
                  onClick={(e) => handleNavigation('/new-batch', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/new-batch') 
                      ? 'bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <PlusCircle size={16} />
                  <span className="font-medium">Trade In</span>
                </Link>
              )}
              
              {/* 4. Queue (previously Buylist) */}
              <Link 
                to={posUrl('/queue')} 
                data-testid="nav-queue"
                onClick={(e) => handleNavigation('/queue', e)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                  isActive('/queue') 
                    ? 'bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-50' 
                    : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                }`}
              >
                <List size={16} />
                <span className="font-medium">Queue</span>
              </Link>
              
              {/* 4.5 Daily Tasks */}
              <Link 
                to={posUrl('/daily-tasks')} 
                data-testid="nav-daily-tasks"
                onClick={(e) => handleNavigation('/daily-tasks', e)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                  isActive('/daily-tasks') 
                    ? 'bg-amber-500 bg-opacity-20 text-amber-400 border border-amber-500 border-opacity-50' 
                    : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                }`}
              >
                <ClipboardCheck size={16} />
                <span className="font-medium">Daily Tasks</span>
                {(pendingLabelCount + pendingCheckCount + pendingPhotoCount) > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-amber-600 text-white rounded-full leading-none" data-testid="nav-daily-tasks-badge">
                    {pendingLabelCount + pendingCheckCount + pendingPhotoCount}
                  </span>
                )}
              </Link>
              
              {/* 5. Repricer */}
              {hasPermission('access_repricer') && (
                <Link 
                  to={posUrl('/repricer')} 
                  data-testid="nav-repricer"
                  onClick={(e) => handleNavigation('/repricer', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/repricer') 
                      ? 'bg-purple-500 bg-opacity-20 text-purple-400 border border-purple-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <RefreshCw size={16} />
                  <span className="font-medium">Repricer</span>
                </Link>
              )}
              
              {/* 6. Inventory */}
              {hasPermission('view_inventory') && (
                <Link 
                  to={posUrl('/inventory')} 
                  data-testid="nav-inventory"
                  onClick={(e) => handleNavigation('/inventory', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/inventory') 
                      ? 'bg-orange-500 bg-opacity-20 text-orange-400 border border-orange-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <Package size={16} />
                  <span className="font-medium">Inventory</span>
                </Link>
              )}
              
              {/* 7. Employee (Employee Zone) */}
              <Link 
                to={posUrl('/employee-zone')} 
                data-testid="nav-employee-zone"
                onClick={(e) => handleNavigation('/employee-zone', e)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm relative ${
                  isActive('/employee-zone') 
                    ? 'bg-cyan-500 bg-opacity-20 text-cyan-400 border border-cyan-500 border-opacity-50' 
                    : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                }`}
              >
                <Users size={16} />
                <span className="font-medium">Employee</span>
                {unreadCount > 0 && (
                  <span 
                    data-testid="unread-badge"
                    className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
              
              {/* 8. Events */}
              <Link 
                to={posUrl('/events')} 
                data-testid="nav-events"
                onClick={(e) => handleNavigation('/events', e)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                  isActive('/events') 
                    ? 'bg-pink-500 bg-opacity-20 text-pink-400 border border-pink-500 border-opacity-50' 
                    : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                }`}
              >
                <CalendarDays size={16} />
                <span className="font-medium">Events</span>
              </Link>

              {/* 9. Analytics */}
              {hasPermission('view_analytics') && (
                <Link 
                  to={posUrl('/analytics')} 
                  data-testid="nav-analytics"
                  onClick={(e) => handleNavigation('/analytics', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/analytics') 
                      ? 'bg-amber-500 bg-opacity-20 text-amber-400 border border-amber-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <BarChart3 size={16} />
                  <span className="font-medium">Analytics</span>
                </Link>
              )}
              
              {/* 9. Settings */}
              {hasPermission('manage_settings') && (
                <Link 
                  to={posUrl('/settings')} 
                  data-testid="nav-settings"
                  onClick={(e) => handleNavigation('/settings', e)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    isActive('/settings') 
                      ? 'bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-50' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-5'
                  }`}
                >
                  <Settings size={16} />
                  <span className="font-medium">Settings</span>
                </Link>
              )}
            </div>
          </div>

          {/* Right side: Chat Room + Location selector + User info */}
          {user && (
            <div className="flex items-center gap-3">
              {/* Chat Room trigger (left of location) */}
              <button
                onClick={openChat}
                data-testid="chat-header-btn"
                title="Open Chat Room"
                className={`relative flex items-center gap-1.5 h-8 px-2.5 rounded-md text-sm transition-colors ${chatFlash ? 'animate-pulse ring-2 ring-pink-500 text-white bg-pink-500 bg-opacity-15' : 'text-gray-300 hover:text-white hover:bg-white hover:bg-opacity-10'}`}
              >
                <MessageSquare size={16} className="text-emerald-400" />
                <span className="hidden sm:inline">Chat Room</span>
                {chatUnreadTotal > 0 && (
                  <span
                    data-testid="chat-header-badge"
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center shadow"
                  >
                    {chatUnreadTotal > 99 ? '99+' : chatUnreadTotal}
                  </span>
                )}
              </button>

              {/* Location Selector */}
              {accessibleLocations.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="location-selector"
                      className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-white hover:bg-opacity-10 h-8"
                      disabled={locationsLoading}
                    >
                      <MapPin size={14} className="text-emerald-400" />
                      <span className="max-w-[80px] sm:max-w-[120px] truncate text-sm">
                        {locationsLoading ? 'Loading...' : (selectedLocation?.name || 'Location')}
                      </span>
                      <ChevronDown size={12} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
                    {accessibleLocations.map((loc) => (
                      <DropdownMenuItem
                        key={loc.id}
                        data-testid={`location-option-${loc.id}`}
                        onClick={() => selectLocation(loc.id)}
                        className={`flex items-center gap-2 cursor-pointer ${
                          loc.id === selectedLocationId 
                            ? 'bg-emerald-600 bg-opacity-20 text-emerald-400' 
                            : 'text-gray-300 hover:bg-slate-700'
                        }`}
                      >
                        <MapPin size={14} />
                        <span className="flex-1 truncate">{loc.name}</span>
                        {loc.is_primary && (
                          <span className="text-xs bg-blue-600 bg-opacity-30 text-blue-400 px-1.5 py-0.5 rounded">
                            Primary
                          </span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* User info — collapse text labels on phones to leave
                  room for the location dropdown + logout button. */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center">
                  <User size={14} className="text-gray-300" />
                </div>
                <div className="text-xs hidden sm:block">
                  <div className="text-white font-medium">{user.full_name}</div>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleBadgeColor(user.role)}`}>
                    {user.role}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-gray-400 hover:text-white hover:bg-red-600 hover:bg-opacity-20 h-8 w-8 p-0"
              >
                <LogOut size={16} />
              </Button>
            </div>
          )}
        </div>
      </nav>
      
      {/* Mobile slide-out nav drawer — phones only. Mirrors the desktop
          nav links (filtered by the same permissions) in a vertical
          stack with bigger tap targets. Auto-closes on route change so
          tapping a link feels instant. */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-50"
          data-testid="mobile-nav-overlay"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            data-testid="mobile-nav-backdrop"
          />
          {/* Drawer */}
          <div
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-slate-900 border-r border-slate-700 shadow-2xl flex flex-col"
            data-testid="mobile-nav-drawer"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <span className="text-base font-semibold text-white">{displayName}</span>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="text-gray-400 hover:text-white p-2 -mr-2 rounded-lg hover:bg-white/5"
                aria-label="Close navigation"
                data-testid="mobile-nav-close"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {hasPermission('use_pos') && (
                <Link to={posUrl('/')} onClick={(e) => handleNavigation('/', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/') ? 'bg-emerald-500/15 text-emerald-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-pos">
                  <ShoppingCart size={18} /> POS
                </Link>
              )}
              {hasPermission('use_pos') && (
                <Link to={posUrl('/orders')} onClick={(e) => handleNavigation('/orders', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/orders') ? 'bg-emerald-500/15 text-emerald-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-orders">
                  <Receipt size={18} /> Orders
                </Link>
              )}
              {(hasPermission('view_customers') || hasPermission('manage_customers')) && (
                <Link to={posUrl('/customers')} onClick={(e) => handleNavigation('/customers', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/customers') ? 'bg-violet-500/15 text-violet-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-customers">
                  <Users size={18} /> Customers
                </Link>
              )}
              {hasPermission('create_batches') && (
                <Link to={posUrl('/new-batch')} onClick={(e) => handleNavigation('/new-batch', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/new-batch') ? 'bg-blue-500/15 text-blue-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-trade-in">
                  <PlusCircle size={18} /> Trade In
                </Link>
              )}
              <Link to={posUrl('/queue')} onClick={(e) => handleNavigation('/queue', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/queue') ? 'bg-blue-500/15 text-blue-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-queue">
                <List size={18} /> Queue
              </Link>
              <Link to={posUrl('/daily-tasks')} onClick={(e) => handleNavigation('/daily-tasks', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/daily-tasks') ? 'bg-amber-500/15 text-amber-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-daily-tasks">
                <ClipboardCheck size={18} /> Daily Tasks
                {(pendingLabelCount + pendingCheckCount + pendingPhotoCount) > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-amber-600 text-white rounded-full">
                    {pendingLabelCount + pendingCheckCount + pendingPhotoCount}
                  </span>
                )}
              </Link>
              {hasPermission('access_repricer') && (
                <Link to={posUrl('/repricer')} onClick={(e) => handleNavigation('/repricer', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/repricer') ? 'bg-purple-500/15 text-purple-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-repricer">
                  <RefreshCw size={18} /> Repricer
                </Link>
              )}
              {hasPermission('view_inventory') && (
                <Link to={posUrl('/inventory')} onClick={(e) => handleNavigation('/inventory', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/inventory') ? 'bg-orange-500/15 text-orange-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-inventory">
                  <Package size={18} /> Inventory
                </Link>
              )}
              <Link to={posUrl('/employee-zone')} onClick={(e) => handleNavigation('/employee-zone', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/employee-zone') ? 'bg-cyan-500/15 text-cyan-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-employee-zone">
                <Users size={18} /> Employee
                {unreadCount > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
              <Link to={posUrl('/events')} onClick={(e) => handleNavigation('/events', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/events') ? 'bg-pink-500/15 text-pink-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-events">
                <CalendarDays size={18} /> Events
              </Link>
              {hasPermission('view_analytics') && (
                <Link to={posUrl('/analytics')} onClick={(e) => handleNavigation('/analytics', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/analytics') ? 'bg-amber-500/15 text-amber-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-analytics">
                  <BarChart3 size={18} /> Analytics
                </Link>
              )}
              {hasPermission('manage_settings') && (
                <Link to={posUrl('/settings')} onClick={(e) => handleNavigation('/settings', e)} className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive('/settings') ? 'bg-blue-500/15 text-blue-300' : 'text-gray-200 hover:bg-white/5'}`} data-testid="mobile-nav-settings">
                  <Settings size={18} /> Settings
                </Link>
              )}
            </nav>
            {/* Footer: user identity + logout */}
            {user && (
              <div className="border-t border-slate-700 p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-gray-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{user.full_name}</div>
                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded mt-0.5 ${getRoleBadgeColor(user.role)}`}>
                    {user.role}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-white hover:bg-red-600/20 h-9 w-9 p-0 flex-shrink-0"
                  data-testid="mobile-nav-logout"
                  aria-label="Logout"
                >
                  <LogOut size={16} />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <main>
        {children}
      </main>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-yellow-400">
              <AlertTriangle className="h-5 w-5" />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              {unsavedMessage || 'You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={cancelNavigation}
              className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
            >
              Stay on Page
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmNavigation}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post-login: prompt the staff member to clock in if they haven't */}
      <ClockInPrompt />

      {/* Staff Chat Room — company-wide room + DMs (right-edge tab + drawer) */}
      <ChatRoom />
    </div>
  );
};

export default Layout;
